// src/pages/RepartitionCompetiteursPage/BracketOrderModal.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaGripVertical,
  FaRandom,
  FaSave,
  FaTimes,
  FaTimesCircle,
  FaTrashAlt,
  FaUndo,
} from "react-icons/fa";

import { updateDistribution } from "../../services/distributionsServices";

const FORMULA_LABELS = {
  tableau_base_8: "Tableau sans repêchage — Base 8",
  tableau_base_16: "Tableau sans repêchage — Base 16",
  tableau_base_32: "Tableau sans repêchage — Base 32",
  tableau_base_64: "Tableau sans repêchage — Base 64",
  tableau_repechage_base_8: "Tableau avec repêchage — Base 8",
  tableau_repechage_base_16: "Tableau avec repêchage — Base 16",
  tableau_repechage_base_32: "Tableau avec repêchage — Base 32",
  tableau_repechage_base_64: "Tableau avec repêchage — Base 64",
};

const Chip = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${className}`}
  >
    {children}
  </span>
);

const DrawerButton = ({
  children,
  icon: Icon,
  onClick,
  variant = "outline",
  disabled = false,
  type = "button",
  className = "",
}) => {
  const variants = {
    outline:
      "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:bg-white/10",
    brand:
      "border border-orange-500 bg-orange-600 text-white hover:bg-orange-700 shadow-lg shadow-orange-500/20",
    danger:
      "border border-rose-500 bg-rose-600 text-white hover:bg-rose-700 shadow-lg shadow-rose-500/20",
    soft:
      "border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-3 py-2
        text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60
        ${variants[variant] || variants.outline}
        ${className}
      `}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      {children}
    </button>
  );
};

const shuffle = (arr) => {
  const a = [...arr];

  for (let i = a.length - 1; i > 0; i -= 1) {
    let j;

    if (window.crypto?.getRandomValues) {
      const randomValues = new Uint32Array(1);
      window.crypto.getRandomValues(randomValues);
      j = randomValues[0] % (i + 1);
    } else {
      j = Math.floor(Math.random() * (i + 1));
    }

    [a[i], a[j]] = [a[j], a[i]];
  }

  return a;
};

const parseCompetiteursIds = (distribution) => {
  if (!distribution) return [];

  const src = distribution.competiteurs;

  if (Array.isArray(src)) return src;

  try {
    const parsed = JSON.parse(src || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeWeight = (value) => {
  if (value == null || value === "") return "—";

  const number = Number(String(value).replace(",", "."));

  return Number.isFinite(number) ? `${number.toFixed(1)} kg` : "—";
};

const getFullName = (competiteur) =>
  `${competiteur?.nom_competiteur || ""} ${
    competiteur?.prenom_competiteur || ""
  }`.trim();

const BracketOrderModal = ({
  isOpen,
  onClose,
  distribution,
  allCompetiteurs = [],
  onSaved,
  onDeleteDistribution,
}) => {
  const dragIndex = useRef(null);

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const orderedIds = useMemo(
    () => parseCompetiteursIds(distribution),
    [distribution]
  );

  const initialRows = useMemo(() => {
    return orderedIds
      .map((rawId) => {
        const id = Number(rawId) || rawId;

        const competiteur = allCompetiteurs.find(
          (item) => String(item.id) === String(id)
        );

        return competiteur || null;
      })
      .filter(Boolean);
  }, [orderedIds, allCompetiteurs]);

  const formulaLabel = useMemo(() => {
    const formula = distribution?.formule_repartition || "";
    return FORMULA_LABELS[formula] || formula || "Tableau";
  }, [distribution]);

  const disciplineLabel = useMemo(() => {
    const disciplines = [
      ...new Set(rows.map((row) => row.discipline_competiteur).filter(Boolean)),
    ];

    if (disciplines.length === 1) return disciplines[0];
    if (disciplines.length > 1) return disciplines.join(" / ");

    return distribution?.discipline || "—";
  }, [rows, distribution]);

  useEffect(() => {
    if (!isOpen) return;

    setRows(initialRows);
    setSaving(false);
  }, [isOpen, initialRows]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const onDragStart = (index) => (event) => {
    dragIndex.current = index;
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = () => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onDrop = (index) => (event) => {
    event.preventDefault();

    const from = dragIndex.current;

    if (from == null || from === index) return;

    setRows((currentRows) => {
      const next = [...currentRows];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });

    dragIndex.current = null;
  };

  const handleReset = () => {
    setRows(initialRows);
  };

  const handleRandom = () => {
    setRows((currentRows) => shuffle(currentRows));
  };

  const handleSave = async () => {
    if (!distribution?.id || saving) return;

    try {
      setSaving(true);

      const newOrderIds = rows.map((row) => row.id);
      const newPositions = rows.map((_, index) => index + 1);

      const payload = {
        id: distribution.id,
        competiteurs: newOrderIds,
        position_aleatoire: newPositions,
      };

      const saved = await updateDistribution(payload);

      if (saved) {
        onSaved?.();
        onClose?.();
      }
    } catch (error) {
      console.error("[BracketOrderModal] Erreur enregistrement :", error);
      alert("Erreur lors de l’enregistrement de l’ordre du tableau.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !distribution) return null;

  return (
    <div
      className="
        fixed inset-0 z-[99999] flex h-[100dvh] w-full items-end justify-center
        overflow-hidden bg-slate-950/65 backdrop-blur-sm
        sm:items-stretch sm:justify-end
      "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Éditer l'ordre du tableau"
        className="
          relative flex h-[92dvh] w-full max-w-full flex-col overflow-hidden
          rounded-t-[2rem] border border-white/20 bg-white text-slate-950 shadow-2xl
          dark:border-white/10 dark:bg-slate-950 dark:text-white
          sm:h-full sm:max-w-[920px] sm:rounded-none sm:rounded-l-[2rem]
        "
      >
        <header className="relative shrink-0 overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 px-4 py-4 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-orange-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-orange-300">
                Tableau
              </p>

              <h2 className="mt-1 text-xl font-bold leading-tight sm:text-2xl">
                Éditer l’ordre des compétiteurs
              </h2>

              <div className="mt-3 flex flex-wrap gap-2">
                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {formulaLabel}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {rows.length} compétiteur{rows.length > 1 ? "s" : ""}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  {disciplineLabel}
                </Chip>

                <Chip className="bg-white/10 text-white ring-1 ring-white/15">
                  Tapis {distribution.numero_tapis ?? "—"}
                </Chip>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="
                inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl
                border border-white/10 bg-white/10 text-white transition
                hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300
              "
              aria-label="Fermer"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="shrink-0 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/95 sm:px-6">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <DrawerButton icon={FaUndo} onClick={handleReset}>
                Réinitialiser
              </DrawerButton>

              <DrawerButton icon={FaRandom} onClick={handleRandom}>
                Mélanger
              </DrawerButton>
            </div>

            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              Glisse une ligne pour modifier l’ordre du tableau.
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-xs">
                <thead className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <tr>
                    <th className="w-8 px-2 py-2 text-left"></th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Pos.
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      ID
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Nom
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Prénom
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Club
                    </th>
                    <th className="px-2 py-2 text-left font-bold uppercase tracking-wide">
                      Poids
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((competiteur, index) => (
                    <tr
                      key={competiteur.id}
                      draggable
                      onDragStart={onDragStart(index)}
                      onDragOver={onDragOver(index)}
                      onDrop={onDrop(index)}
                      className="
                        border-t border-slate-100 transition hover:bg-slate-50
                        dark:border-white/10 dark:hover:bg-white/5
                      "
                    >
                      <td className="px-2 py-1.5">
                        <span className="inline-flex cursor-move items-center justify-center text-slate-400">
                          <FaGripVertical className="h-3.5 w-3.5" />
                        </span>
                      </td>

                      <td className="px-2 py-1.5 font-bold">{index + 1}</td>

                      <td className="px-2 py-1.5 text-slate-500 dark:text-slate-400">
                        {competiteur.id}
                      </td>

                      <td className="px-2 py-1.5 font-bold">
                        {competiteur.nom_competiteur}
                      </td>

                      <td className="px-2 py-1.5">
                        {competiteur.prenom_competiteur}
                      </td>

                      <td
                        className="max-w-[260px] truncate px-2 py-1.5"
                        title={competiteur.club_competiteur}
                      >
                        {competiteur.club_competiteur || "—"}
                      </td>

                      <td className="px-2 py-1.5">
                        <Chip className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                          {safeWeight(competiteur.poids_competiteur)}
                        </Chip>
                      </td>
                    </tr>
                  ))}

                  {rows.length === 0 && (
                    <tr>
                      <td
                        className="px-3 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                        colSpan={7}
                      >
                        Aucun compétiteur dans cette distribution.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Le bouton “Mélanger” refait un tirage aléatoire local. L’ordre sera
            sauvegardé uniquement après clic sur “Enregistrer l’ordre”.
          </p>
        </div>

        <footer className="shrink-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/95 sm:px-6">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            {onDeleteDistribution && (
              <DrawerButton
                icon={FaTrashAlt}
                onClick={() => onDeleteDistribution(distribution)}
                variant="danger"
              >
                Supprimer
              </DrawerButton>
            )}

            <DrawerButton icon={FaTimesCircle} onClick={onClose}>
              Annuler
            </DrawerButton>

            <DrawerButton
              icon={FaSave}
              onClick={handleSave}
              variant="brand"
              disabled={saving}
            >
              {saving ? "Enregistrement..." : "Enregistrer l’ordre"}
            </DrawerButton>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default BracketOrderModal;